<template>
  <div 
    v-show="visible" 
    class="floating-controls"
    :style="position"
    @click.stop
    @mousedown.stop="handleMouseDown"
  >
    <!-- 局部变速 -->
    <div v-if="activeControl === 'speed'" class="control-panel">
      <div class="slider-container">
        <span>低</span>
        <el-slider
          class="slider"
          v-model="speedValue"
          :min="50"
          :max="200"
          :step="10"
        />
        <span>高</span>
      </div>
      <div class="actions">
        <button class="confirm-btn" @click="handleSpeedConfirm">确认</button>
      </div>
    </div>

    <!-- 局部变调 -->
    <div v-if="activeControl === 'tone'" class="control-panel">
      <div class="slider-container">
        <span>低</span>
        <el-slider
          class="slider"
          v-model="toneValue"
          :min="50"
          :max="200"
          :step="10"
        />
        <span>高</span>
      </div>
      <div class="actions">
        <button class="confirm-btn" @click="handleToneConfirm">确认</button>
      </div>
    </div>

    <!-- 多音字控制 -->
    <div v-if="activeControl === 'phoneme'" class="control-panel">
      <div class="header">
        <span>设置多音字</span>
        <span class="value">{{ selectedChar }}</span>
      </div>
      <div class="phoneme-inputs">
        <div class="input-group">
          <label>拼音：</label>
          <input 
            v-model="phonemeValue.pinyin" 
            type="text" 
            placeholder="输入拼音" 
            @input="handlePinyinInput"
            @mousedown.stop
            @click.stop
            @focus.stop
          />
        </div>
        <div class="input-group">
          <label>声调：</label>
          <input 
            v-model="phonemeValue.tone" 
            type="text" 
            placeholder="1-5" 
            maxlength="1"
            @input="handleToneInput"
            @mousedown.stop
            @click.stop
            @focus.stop
          />
        </div>
      </div>
      <div class="actions">
        <button
          class="confirm-btn"
          @mousedown.stop
          @click.stop="handlePhonemeConfirm"
        >
          确认
        </button>
        <button class="cancel-btn" @click="hide">取消</button>
      </div>
    </div>
  </div>
</template>

<script>
import { Slider } from 'element-ui'

export default {
  name: 'FloatingControls',

  components: {
    'el-slider': Slider
  },

  data() {
    return {
      visible: false,
      activeControl: null,
      position: {
        top: '0px',
        left: '0px'
      },
      speedValue: 100,
      toneValue: 100,
      savedRange: null,
      clickOutsideHandler: null,
      selectionChangeHandler: null,
      resizeHandler: null,
      selectedChar: '',
      phonemeValue: {
        pinyin: '',
        tone: ''
      }
    }
  },

  methods: {
    showSpeed(range, position) {
      this.activeControl = 'speed'
      this.speedValue = 100
      this.setPosition(range, position)
      this.setupEventListeners()
    },

    showTone(range, position) {
      this.activeControl = 'tone'
      this.toneValue = 100
      this.setPosition(range, position)
      this.setupEventListeners()
    },

    showPhoneme(range, position) {
      this.activeControl = 'phoneme';
      this.savedRange = range;
      
      // 获取选中的汉字
      this.selectedChar = range.toString();
      
      // 重置拼音和声调
      this.phonemeValue = { pinyin: '', tone: '' };
      
      this.setPosition(range, position);
      
      // 在下一个DOM更新周期，聚焦到拼音输入框
      this.$nextTick(() => {
        const pinyinInput = this.$el.querySelector('.phoneme-inputs input:first-child');
        if (pinyinInput) {
          pinyinInput.focus();
        }
      });
    },

    setPosition(range, customPosition) {
      this.savedRange = range
      
      if (customPosition) {
        this.position = customPosition
        this.visible = true
        return
      }
      
      // 根据选区计算位置
      try {
        const rect = range.getBoundingClientRect()
        const editorRect = document.getElementById('article').getBoundingClientRect()
        
        // 控件宽度 (保守估计为320px，或者你可以在mounted后获取实际宽度)
        const controlWidth = 320
        
        // 初始计算位置（相对于编辑器）
        let left = rect.left - editorRect.left
        const top = rect.bottom - editorRect.top + 30
        
        // 检查右侧边界 - 确保控件不会超出编辑器
        if (left + controlWidth > editorRect.width) {
          // 如果超出右侧，则向左移动
          // 策略1: 尝试右对齐
          left = Math.max(0, editorRect.width - controlWidth - 10)
          
          // 策略2: 如果选区足够宽，可以考虑中心对齐
          if (rect.width > 100) {
            const centerAlign = rect.left + (rect.width / 2) - (controlWidth / 2) - editorRect.left
            // 只有当中心对齐不会导致超出左右边界时才使用
            if (centerAlign >= 0 && centerAlign + controlWidth <= editorRect.width) {
              left = centerAlign
            }
          }
        }
        
        // 设置最终位置
        this.position = {
          top: `${top}px`,
          left: `${left}px`
        }
        
        this.visible = true
        
        // 在下一个事件循环检查位置，确保位置计算后DOM已更新
        this.$nextTick(() => {
          this.adjustPositionIfNeeded()
        })
      } catch (e) {
        console.error('计算控件位置出错', e)
        this.visible = false
      }
    },

    setupEventListeners() {
      // 移除现有的事件监听器
      this.removeEventListeners()

      // 监听点击其他区域
      this.clickOutsideHandler = (e) => {
        // 如果点击的是控件内部，不做任何处理
        if (this.$el.contains(e.target)) {
          return;
        }
        
        // 使用setTimeout延迟处理，让输入框焦点先处理
        setTimeout(() => {
          // 再次检查焦点是否在输入框
          const activeElement = document.activeElement;
          const isInputFocused = this.$el.contains(activeElement) && 
                                activeElement.tagName === 'INPUT';
                                
          // 如果焦点在输入框，不隐藏控件
          if (isInputFocused) {
            return;
          }
          
          // 检查点击的是否是编辑器的工具栏
          const isToolbarClick = e.target.closest('.operators') || 
                              e.target.closest('.toolbar-wrapper');
          
          // 如果不是控件内部且不是工具栏，则隐藏控件
          if (this.visible && !isToolbarClick) {
            this.hide();
          }
        }, 0);
      }
      
      // 监听选区变化
      this.selectionChangeHandler = () => {
        // 如果正在输入中，不处理选区变化
        if (document.activeElement && this.$el.contains(document.activeElement)) {
          return;
        }
        
        // 如果选区变化，检查是否与保存的选区不同
        const selection = window.getSelection()
        if (selection.rangeCount > 0) {
          const currentRange = selection.getRangeAt(0)
          // 如果选区变化，隐藏控件
          if (this.savedRange && 
              (currentRange.startContainer !== this.savedRange.startContainer ||
               currentRange.startOffset !== this.savedRange.startOffset ||
               currentRange.endContainer !== this.savedRange.endContainer ||
               currentRange.endOffset !== this.savedRange.endOffset)) {
            this.hide()
          }
        }
      }

      // 使用捕获阶段，保证先于其他点击事件处理
      document.addEventListener('mousedown', this.clickOutsideHandler, true)
      document.addEventListener('selectionchange', this.selectionChangeHandler, true)
    },

    removeEventListeners() {
      if (this.clickOutsideHandler) {
        document.removeEventListener('mousedown', this.clickOutsideHandler, true)
        this.clickOutsideHandler = null
      }
      
      if (this.selectionChangeHandler) {
        document.removeEventListener('selectionchange', this.selectionChangeHandler, true)
        this.selectionChangeHandler = null
      }
    },

    hide() {
      // 检查是否有输入框处于焦点状态
      const activeElement = document.activeElement;
      const isInputFocused = this.$el && this.$el.contains(activeElement) && 
                            activeElement.tagName === 'INPUT';
                            
      // 如果有输入框处于焦点状态，不隐藏控件
      if (isInputFocused) {
        return;
      }
      
      this.visible = false
      this.savedRange = null
      this.activeControl = null
      this.removeEventListeners()
    },

    handleSpeedConfirm() {
      if (!this.savedRange) return
      
      this.$emit('speed-confirm', {
        attrs: {
          rate: this.speedValue / 100,
          'display-type': `语速:${this.speedValue}`
        },
        range: this.savedRange
      })
      
      this.hide()
    },

    handleToneConfirm() {
      if (!this.savedRange) return
      
      this.$emit('tone-confirm', {
        attrs: {
          pitch: this.toneValue / 100,
          'display-type': `语调:${this.toneValue}%`
        },
        range: this.savedRange
      })
      
      this.hide()
    },

    handlePinyinInput(e) {
      // 将拼音自动转为大写，仅保留英文字母
      this.phonemeValue.pinyin = this.phonemeValue.pinyin
        .toUpperCase()
        .replace(/[^A-Z]/g, '');
    },
    
    handleToneInput(e) {
      // 仅允许输入1-5的数字
      this.phonemeValue.tone = this.phonemeValue.tone
        .replace(/[^1-5]/g, '');
      
      // 确保只有一位数字
      if (this.phonemeValue.tone.length > 1) {
        this.phonemeValue.tone = this.phonemeValue.tone.charAt(0);
      }
    },
    
    handlePhonemeConfirm() {
      console.log(this.phonemeValue)
      if (!this.phonemeValue.pinyin) return;
      const tone = this.phonemeValue.tone;
      if (tone && (isNaN(tone) || +tone < 1 || +tone > 5)) return;

      this.$emit('confirm', {
        pinyin: this.phonemeValue.pinyin,
        tone: this.phonemeValue.tone,
        char: this.selectedChar,
        range: this.savedRange
      });
      this.hide();
    },

    // 新增方法：调整控件位置以避免超出可视区域
    adjustPositionIfNeeded() {
      if (!this.visible || !this.$el) return
      
      const controlRect = this.$el.getBoundingClientRect()
      const editorRect = document.getElementById('article').getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      
      let newLeft = parseFloat(this.position.left)
      let newTop = parseFloat(this.position.top)
      let needsUpdate = false
      
      // 检查右侧是否超出视口
      if (controlRect.right > viewportWidth) {
        newLeft = Math.max(0, newLeft - (controlRect.right - viewportWidth) - 10)
        needsUpdate = true
      }
      
      // 检查右侧是否超出编辑器
      if (controlRect.right > editorRect.right) {
        newLeft = Math.max(0, newLeft - (controlRect.right - editorRect.right) - 10)
        needsUpdate = true
      }
      
      // 检查底部是否超出视口
      if (controlRect.bottom > viewportHeight) {
        // 如果底部超出，尝试在选区上方显示
        const selectionRect = this.savedRange.getBoundingClientRect()
        const aboveSpace = selectionRect.top - editorRect.top - controlRect.height - 5
        
        if (aboveSpace > 0) {
          // 在选区上方显示
          newTop = aboveSpace
        } else {
          // 如果上方空间不足，尽量减少超出底部的部分
          newTop = Math.max(0, newTop - (controlRect.bottom - viewportHeight) - 10)
        }
        needsUpdate = true
      }
      
      // 如果需要更新位置
      if (needsUpdate) {
        this.position = {
          top: `${newTop}px`,
          left: `${newLeft}px`
        }
      }
    },

    // 处理鼠标按下事件
    handleMouseDown(e) {
      // 阻止事件冒泡，确保不会触发外部的点击处理
      e.stopPropagation();
    }
  },

  mounted() {
    // 添加窗口大小变化监听
    this.resizeHandler = () => {
      if (this.visible) {
        this.adjustPositionIfNeeded()
      }
    }
    window.addEventListener('resize', this.resizeHandler)
  },

  beforeDestroy() {
    // 移除窗口大小变化监听
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
    this.removeEventListeners()
  }
}
</script>

<style lang="less" scoped>
.floating-controls {
  position: absolute;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  max-width: 95%; /* 防止溢出编辑区 */
  
  .control-panel {
    background: white;
    box-shadow: 4px 4px 20px 0px rgba(5, 10, 15, 0.1);
    border-radius: 4px;
    padding: 12px;
    /* 防止控件被遮挡 */
    transform-origin: top left;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    
    .header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
      font-weight: bold;
      
      .value {
        color: #41B48C;
      }
    }
    
    .slider-container {
      display: flex;
      align-items: center;
      // margin-bottom: 15px;
      width: 300px;
      
      span {
        margin: 0 10px;
      }
      
      .slider {
        flex: 1;

        /deep/.el-slider__bar {
          background: #41B48C;
        }

        /deep/.el-slider__button {
          border-color: #41B48C;
        }
      }
    }
    
    .actions {
      display: flex;
      justify-content: flex-end;
      
      button {
        padding: 6px 15px;
        border-radius: 4px;
        border: none;
        cursor: pointer;
        margin-left: 8px;
        font-size: 14px;
      }
      
      .confirm-btn {
        background: #41B48C;
        color: white;
      }
      
      .cancel-btn {
        background: #B9C3C3;
        color: white;
      }
    }
  }

  .phoneme-inputs {
    margin-bottom: 15px;
    
    .input-group {
      display: flex;
      align-items: center;
      margin-bottom: 10px;
      
      label {
        flex: 0 0 60px;
      }
      
      input {
        flex: 1;
        padding: 5px 8px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        
        &:focus {
          border-color: #41B48C;
          outline: none;
        }
      }
    }
  }
}
</style>
